import React, { Fragment, useRef, useState, useEffect } from 'react';
import { translate } from '../Services/translation';

const NavigationItem = ({
    links = [],
    contentLink = null,
    level = 0,
    parentMenuOpen = false,
    isSubMenuOpen = false
}) => {
    let globalMenuIdCounter = 0;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const menuRef = useRef(null);
    const linkRef = useRef(null);
    const menuIdRef = useRef(`menu-${globalMenuIdCounter++}`);
    const hasChildren = links.length > 0;
    const mobileWidth = 1024;

    useEffect(() => {
        if (!isMenuOpen || window.innerWidth > mobileWidth) return;

        const menu = menuRef.current;
        if (!menu) return;

        const focusable = menu.querySelectorAll('a[href],button,[tabindex]:not([tabindex="-1"]),i[tabindex="0"]');
        if (!focusable.length) return;

        const handleKeyDown = (e) => {
            if (e.key !== 'Tab') return;

            const first = focusable[0], last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        menu.addEventListener('keydown', handleKeyDown);
        return () => menu.removeEventListener('keydown', handleKeyDown);
    }, [isMenuOpen]);


    const getTabIndex = () => {
        if (level === 1) return 0;
        return isMenuOpen || parentMenuOpen || isSubMenuOpen ? 0 : -1;
    };

    const focusFirstTopLinkIfNeeded = () => {
        if (window.innerWidth <= mobileWidth && level === 1) {
            const firstTopLink = document.querySelector('.navbar__menu.level-0 .navbar__link.level-1');
            firstTopLink?.focus();
        }
    };

    const handleEnterSpace = (e, action) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        action();
    };

    const toggleMenu = (e, toggleIsMenuOpen) => {
        e.preventDefault();
        if (toggleIsMenuOpen) {
            setIsMenuOpen((prev) => !prev);
        }
    };
    const focusAndToggleMenu = () => {
        setIsMenuOpen(prev => !prev);
        setIsFocused(true);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        setIsFocused(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (contentLink?.url) return;
            e.preventDefault();
            if (hasChildren) {
                focusAndToggleMenu();
            }
        }
        if (e.key === ' ') {
            e.preventDefault();
            if (hasChildren) {
                focusAndToggleMenu();
            }
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            if (window.innerWidth <= mobileWidth) {
                const rootMenu = document.querySelector('.navbar__menu.level-0');
                if (rootMenu) {
                    rootMenu.classList.remove('navbar__menu--show', 'show-desktop-menu');
                    if (rootMenu.previousElementSibling) {
                        rootMenu.previousElementSibling.className =
                            "navbar__link--block navbar__icon--menu navbar__icon";
                    }
                }
                closeMenu();
                return;
            }
            if (level === 1) {
                closeMenu();
                return;
            }
            closeMenu();
            focusFirstTopLinkIfNeeded();
        }
    };

    const handleBlur = (e) => {
        if (window.innerWidth <= mobileWidth) return;
        if (menuRef.current && menuRef.current.contains(e.relatedTarget)) return;
        closeMenu();
    };

    const hasNameOrChildren = (link) => link.name || (link.links || []).length > 0;

    const focusClass = isFocused ? 'navbar__link--focus' : '';
    const openClass = isMenuOpen ? 'navbar__link--open' : '';
    const additionClass = contentLink && contentLink.attributes ? contentLink.attributes.cssValue : null;
    const selectedClass = contentLink && contentLink.isSelected ? 'navbar__link--selected' : '';
    const hasChildrenClass = hasChildren ? 'has-children' : '';
    const menuFocusClass = (level >= 1 && isMenuOpen && isFocused) ? 'isFocused' : '';
    const menuOpenClass = isMenuOpen ? 'show-desktop-menu navbar__menu--show' : '';

    return (
        <Fragment>
            {!contentLink ? (
                <a
                    className={`navbar__link--block navbar__icon navbar__icon--menu`}
                    onClick={(e) => toggleMenu(e, true)}
                    rel="nofollow"
                    href="#"
                    title={translate('general.menu')}
                    tabIndex={0}
                    aria-label={translate('general.menu')}
                    onKeyDown={handleKeyDown}
                ></a>
            ) : (
                <Fragment>
                    <a
                        className={`navbar__link level-${level} ${selectedClass} ${hasChildrenClass} ${additionClass || ''} ${focusClass} ${openClass}`}
                        href={contentLink.url || '#'}
                        tabIndex={getTabIndex()}
                        aria-haspopup={hasChildren ? 'true' : undefined}
                        aria-expanded={hasChildren ? isMenuOpen : undefined}
                        aria-controls={hasChildren ? menuIdRef.current : undefined}
                        dangerouslySetInnerHTML={{ __html: contentLink.name }}
                        ref={linkRef}
                        onFocus={() => setIsFocused(true)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => {
                            if (contentLink?.url) return;
                            if (hasChildren) {
                                e.preventDefault();
                                focusAndToggleMenu();
                            }
                        }}
                    ></a>
                    {hasChildren && (
                        <i
                            className="navbar__icon--caret-right navbar__icon navbar__icon--open"
                            onClick={(e) => {
                                e.preventDefault();
                                focusAndToggleMenu();
                            }}
                            onKeyDown={handleKeyDown}
                            tabIndex={0}
                            aria-hidden="true"
                        ></i>
                    )}
                </Fragment>
            )}

            {hasChildren && (
                <div
                    className={`navbar__menu level-${level} ${menuFocusClass} ${menuOpenClass}`}
                    ref={menuRef}
                    id={contentLink ? menuIdRef.current : undefined}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                >
                    <div className="navbar__menu-header">
                        {level >= 1 && (
                            <Fragment>
                                <i
                                    className="navbar__icon--caret-left navbar__icon"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        closeMenu();
                                        focusFirstTopLinkIfNeeded();
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={translate('general.back')}
                                    onKeyDown={(e) =>
                                        handleEnterSpace(e, () => {
                                            closeMenu();
                                            focusFirstTopLinkIfNeeded();
                                        })
                                    }
                                ></i>
                                <span
                                    className="navbar__title"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={contentLink.name}
                                    dangerouslySetInnerHTML={{
                                        __html: contentLink.name,
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        closeMenu();
                                        focusFirstTopLinkIfNeeded();
                                    }}
                                    onKeyDown={(e) =>
                                        handleEnterSpace(e, () => {
                                            closeMenu();
                                            focusFirstTopLinkIfNeeded();
                                        })
                                    }
                                ></span>
                            </Fragment>
                        )}
                        <span
                            className="navbar__icon navbar__icon--close"
                            onClick={() => {
                                closeMenu();
                                focusFirstTopLinkIfNeeded();
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={translate('general.close')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    closeMenu();
                                    focusFirstTopLinkIfNeeded();

                                }
                            }}
                        ></span>
                    </div>
                    <ul className="navbar__menu-links">
                        {links.map((link, index) =>
                            hasNameOrChildren(link) && (
                                <li className="navbar__item" key={index}>
                                    <NavigationItem
                                        links={link.links}
                                        contentLink={link}
                                        level={level + 1}
                                        parentMenuOpen={isMenuOpen}
                                        isSubMenuOpen={parentMenuOpen}
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>
            )}
        </Fragment>
    );
};

export default NavigationItem;